/*
 * The conversation and membership routes of protocol.md §3.2 — creating a channel, browsing the
 * public ones, joining and leaving, renaming one and rewriting what it is about, adding people and
 * taking one out, and opening a direct conversation — plus what the conversation's own views read
 * beside its timeline: one page of a thread and the message one permalink names, its pins and the
 * pin switch, and its files — and the one core route the first-channel door reads, the version of the
 * default-channel setting. `../api.ts` holds the timeline itself (history and the markdown
 * preview); the split is by subject, and both go through core's `api`, which is where the CSRF
 * header, `no-store` and the one error type live. A view that needs a route calls a function here;
 * no component spells a path (`../api.test.ts` holds every route of the module to one of these files).
 *
 * The answers are the generated wire types with the conversation narrowed to the four kinds the
 * schema allows (`wire/views.ts`), so what a dialog puts into the store is the same shape the
 * bootstrap put there.
 */

import { api } from "../../../core/net/api.ts"
import type { ConversationId, GroupId, MessageId } from "../../../core/store/model.ts"
import type {
	FilePage,
	ChannelSummary as GeneratedChannelSummary,
	ConversationCreated as GeneratedConversationCreated,
	ConversationResponse as GeneratedConversationResponse,
	DmOpened as GeneratedDmOpened,
	MembersAdded as GeneratedMembersAdded,
	PinnedPage as GeneratedPinnedPage,
	MemberSelf,
	MessageRead,
	PinView,
	SetAgents,
	UpdateConversation,
} from "../wire/chat.gen.ts"
import type {
	AdminConversation,
	ConversationReadAnswer,
	ConversationView,
	HistoryPage,
	MessageView,
} from "../wire/views.ts"

/**
 * How much history people added to a conversation may read. The generated `AddMembers.history` is a
 * `string` — tygo renders the Go const block that way — so the two values the server validates are
 * spelled here, and a dialog cannot offer a third.
 */
export type HistoryMode = "full" | "fromNow"

/** The server's own page cap (`Limits.historyPageMax`), as `../api.ts` asks for it. */
const THREAD_PAGE = 50

/*
 * The three caps of `internal/modules/chat/model.go` — in characters, which is how `boundedText`
 * counts them and how a person experiences a limit. A box that accepts more than the route does
 * turns a sentence somebody wrote into a refusal, so the field carries the cap and the server
 * confirms it. The short name's own cap lives with its rule in `../sidebar/slug.ts`.
 */
export const NAME_MAX = 80
export const TOPIC_MAX = 250
export const PURPOSE_MAX = 2000

export interface ConversationCreated extends Omit<GeneratedConversationCreated, "conversation"> {
	conversation: ConversationView
}

export interface ChannelSummary extends Omit<GeneratedChannelSummary, "conversation"> {
	conversation: ConversationView
}

export interface MembersAdded extends Omit<GeneratedMembersAdded, "conversation"> {
	conversation: ConversationView
}

export interface DmOpened extends Omit<GeneratedDmOpened, "conversation"> {
	conversation: ConversationView
}

export interface ConversationResponse extends Omit<GeneratedConversationResponse, "conversation"> {
	conversation: ConversationView
}

/**
 * What the new-channel dialog collects. Only a channel or a private conversation is created here.
 * `ownerGroupIds` names the groups a channel belongs to; absent, the server gives it the team
 * members group, which is why the dialog's default choice sends nothing rather than the team's id.
 */
export interface CreateChannelInput {
	kind: "channel" | "private"
	slug: string
	name: string
	topic: string
	memberIds: number[]
	ownerGroupIds?: GroupId[]
}

export function createConversation(input: CreateChannelInput): Promise<ConversationCreated> {
	return api.post<ConversationCreated>("/api/chat/conversations", {
		kind: input.kind,
		slug: input.slug,
		name: input.name,
		topic: input.topic,
		memberIds: input.memberIds,
		...(input.ownerGroupIds === undefined ? {} : { ownerGroupIds: input.ownerGroupIds }),
	})
}

/**
 * One conversation's head and members, with how the reader holds it (`reach`): their own row, a
 * group that owns the channel, or — for an administrator outside a channel or private channel —
 * the administrator's view, which never includes its messages.
 */
export function readConversation(conv: ConversationId): Promise<ConversationReadAnswer> {
	return api.get<ConversationReadAnswer>(`/api/chat/conversations/${String(conv)}`)
}

/**
 * The administrator's listing: every private channel and every channel the administrator's groups
 * do not reach, by name and owner groups, never by who is in it. Nothing else reads it but the
 * administrator's Browse and the address that falls back to it.
 */
export function listAdminConversations(): Promise<{ conversations: AdminConversation[] }> {
	return api.get<{ conversations: AdminConversation[] }>("/api/admin/chat/conversations")
}

/**
 * The same listing asked for the channels and private channels open to AI chats (`?agents=open`),
 * whether or not the administrator is in them: what Claude may read, which the *Channels* part of
 * Administration ▸ *Open to AI chats* lists.
 */
export function listAgentsOpenConversations(): Promise<{ conversations: AdminConversation[] }> {
	return api.get<{ conversations: AdminConversation[] }>(
		"/api/admin/chat/conversations?agents=open",
	)
}

/** Opens a channel to more groups; one call for every new id, answered with the channel's view. */
export function addOwners(
	conv: ConversationId,
	groupIds: GroupId[],
): Promise<ConversationResponse> {
	return api.post<ConversationResponse>(`/api/chat/conversations/${String(conv)}/owners`, {
		groupIds,
	})
}

/** Takes one group off a channel; the server refuses the last live owner (`error.chat.lastOwner`). */
export function removeOwner(conv: ConversationId, groupId: GroupId): Promise<ConversationResponse> {
	return api.del<ConversationResponse>(
		`/api/chat/conversations/${String(conv)}/owners/${String(groupId)}`,
	)
}

/**
 * Opens a conversation to AI chats, or closes it: Claude in an AI chat whose owners could all open
 * it may read it while it is on. The server writes the line everyone in the conversation sees.
 */
export function setAgents(conv: ConversationId, available: boolean): Promise<ConversationResponse> {
	return api.put<ConversationResponse>(`/api/chat/conversations/${String(conv)}/agents`, {
		available,
	} satisfies SetAgents)
}

/**
 * Every channel the reader's groups can find, joined or not, and the ones the reader is in — the
 * list the join button sits on. `slug` asks the same question about one exact address instead,
 * which is what a route resolving `/c/<slug>` needs: a substring page of fifty cannot promise that
 * the channel it was given is in it (B2-F R2). A private conversation is never in either answer:
 * browsing is `chat.conversation.browse`, and the server answers what that allows.
 */
export function browseChannels(q: string, slug?: string): Promise<{ channels: ChannelSummary[] }> {
	const params = new URLSearchParams()
	if (slug !== undefined && slug !== "") params.set("slug", slug)
	else if (q !== "") params.set("q", q)
	const query = params.toString()
	return api.get<{ channels: ChannelSummary[] }>(
		query === "" ? "/api/chat/channels" : `/api/chat/channels?${query}`,
	)
}

export function joinConversation(conv: number): Promise<{ self: MemberSelf }> {
	return api.post<{ self: MemberSelf }>(`/api/chat/conversations/${conv}/join`)
}

export function leaveConversation(conv: number): Promise<void> {
	return api.post<void>(`/api/chat/conversations/${conv}/leave`)
}

/**
 * Adds people. `history` is the one decision the caller has to make: the conversation's default
 * window, or nothing that happened before the add — which is why the dialog asks rather than
 * choosing (spec 05 §2.4).
 */
export function addMembers(
	conv: number,
	userIds: number[],
	history: HistoryMode,
): Promise<MembersAdded> {
	return api.post<MembersAdded>(`/api/chat/conversations/${conv}/members`, { userIds, history })
}

/**
 * Rewrites what a conversation is called and what it is about. Every field is optional and an
 * absent one is left alone, so the About block sends the one line it changed and the rename dialog
 * sends both names — one route, one audit action, one system message per field that really moved.
 */
export function updateConversation(
	conv: number,
	patch: UpdateConversation,
): Promise<ConversationResponse> {
	return api.patch<ConversationResponse>(`/api/chat/conversations/${conv}`, patch)
}

/**
 * Takes one person out of a conversation. Leaving is a different route (`leaveConversation`), which
 * is why this one is never aimed at oneself: the two are different decisions and the stream says so
 * with two different system messages.
 */
export function removeMember(conv: number, userId: number): Promise<void> {
	return api.del<void>(`/api/chat/conversations/${conv}/members/${userId}`)
}

/** Get-or-create: the same people always land in the same direct conversation. */
export function openDirectMessage(userIds: number[]): Promise<DmOpened> {
	return api.post<DmOpened>("/api/chat/dm", { userIds })
}

/**
 * One page of a thread: the replies alone, with the root itself beside them. `wire/views.ts`
 * narrows a history page's message list to the rendered union; the root is a message too and needs
 * the same narrowing, which is all this shape adds.
 */
export interface ThreadPage extends Omit<HistoryPage, "root"> {
	root?: MessageView
}

/**
 * One page of a thread: the replies of `rootId`, oldest first, with the root in `root` whatever the
 * page is scrolled to. `before` pages further into the past, the same way the timeline's own
 * `fetchBefore` does; without it the newest page comes back, which is where a pane always opens.
 */
export function fetchThreadPage(
	conv: number,
	rootId: number,
	before?: number,
): Promise<ThreadPage> {
	const params = new URLSearchParams()
	if (before !== undefined) params.set("before", String(before))
	params.set("limit", String(THREAD_PAGE))
	return api.get<ThreadPage>(
		`/api/chat/conversations/${conv}/threads/${rootId}?${params.toString()}`,
	)
}

/**
 * The message one permalink names. The timeline loads its *page* through `fetchAround`; this reads
 * the single row, which is what says whether the seq belongs to a reply and which thread to open.
 */
export function fetchMessageBySeq(conv: number, seq: number): Promise<MessageRead> {
	return api.get<MessageRead>(`/api/chat/conversations/${conv}/messages/${seq}`)
}

// ---- the pins (X-A4 N1) ----

/** The list route's body, with the tree typed as the closed union the renderer switches on. */
export interface PinnedPage extends Omit<GeneratedPinnedPage, "pinned"> {
	pinned: { pin: PinView; message: MessageView }[]
}

/** A conversation's pins, newest first, each with the message behind it (`pins.ts` keeps them). */
export function fetchPinned(conv: ConversationId): Promise<PinnedPage> {
	return api.get<PinnedPage>(`/api/chat/conversations/${String(conv)}/pins`)
}

/**
 * Pins or unpins one message. The answer every reader draws from is the `chat.pin.changed` event
 * the server publishes to the conversation, which `pins.ts` applies — so nothing is written into a
 * store here, and the two browsers of a round trip see the same list. A refusal therefore needs no
 * rollback: the list on screen is the server's own last word.
 */
export function setPinned(messageId: MessageId, on: boolean): Promise<void> {
	const path = `/api/chat/messages/${String(messageId)}/pin`
	return on ? api.put<unknown>(path).then(() => undefined) : api.del<void>(path)
}

// ---- the files (X-A4 N13) ----

/**
 * One page of a conversation's files, newest first, each row the attachment flattened together with
 * the message it was sent with (`wire.FilePage`). Without `before` it is the newest page; with it,
 * the page below the row whose id it names — ids never tie, stamps do.
 */
export function fetchFilesPage(conv: ConversationId, before?: number): Promise<FilePage> {
	const query = before === undefined ? "" : `?before=${before}`
	return api.get<FilePage>(`/api/chat/conversations/${conv}/files${query}`)
}

// ---- the default channel ----

/**
 * The version of one instance setting as `GET /api/admin/settings` answers it, or `undefined` when
 * the server registers no such key. A write names the version it read (`ifVersion`, 0 for a key
 * nobody has written yet), and a key that is not registered is not written at all — which is the
 * difference core's `adminApi.readDefaultChannel` folds away, and the first-channel door needs
 * (`home/CreateFirstChannel.tsx`).
 */
export async function readSettingVersion(key: string): Promise<number | undefined> {
	const body = await api.get<{ settings: { key: string; version: number }[] }>(
		"/api/admin/settings",
	)
	return body.settings.find((setting) => setting.key === key)?.version
}
